import { Empty, Modal } from 'antd';
import LotInfoCard from 'components/LotInfoCard';
import { setSelectedLotId } from 'features/siteMapSlice';
import gaFunction from 'lib/ga';
import { useSiteMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import { getLotTitle } from 'lib/utils';
import { useEffect } from 'react';
import Carousel from 'react-multi-carousel';

const LotModal = () => {
  // Redux Hooks and States
  const dispatch = useAppDispatch();
  const { selectedLotId, isolatePlan, userData } = useAppSelector((state) => ({
    selectedLotId: state.siteMap.selectedLotId,
    isolatePlan: state.siteMap.isolatePlan,
    userData: state.siteMap.userData,
  }));

  // Get Site Map Data from React-Query Cache (No Params)
  const siteMapData = useSiteMapQuery().data!;
  // Semantic Vars From Site Map Data
  const {
    elevations,
    lots,
    displayOptions: [displayOptions],
    applicationURL: { BASE_ASSET_URL },
    community: { applicationID: communityId },
    orientation,
    plans,
    phases,
  } = siteMapData;

  // Constants
  const isMobileView = window.innerWidth < 576 || window.innerHeight < 576;
  const responsive = {
    desktop: {
      breakpoint: { max: 10000, min: 1024 },
      items: 1,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 1,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
    },
  };
  const URLParams = new URLSearchParams(window.location.search);
  const filterPlanSysId = URLParams.get('filterPlanSysId');

  // React Hooks
  // Google Analytics
  useEffect(() => {
    if (selectedLotId && planElvs[0] && elevations[planElvs[0].comElevationsSysId].style) {
      const elevationName = elevations[planElvs[0].comElevationsSysId].style;
      gaFunction.spViewLotElevation(lotTitle, elevationName, 'true');
    }
    // eslint-disable-next-line
  }, [selectedLotId]);

  // Conditions
  if (!selectedLotId) {
    return <></>;
  }

  // Conditional Constants
  // TODO: do we need to use '?', how to handle when clicking a unit
  const planElvs = lots[selectedLotId]?.planElvs;

  const lotTitle = getLotTitle({
    instanceName: selectedLotId,
    lotPrefix: displayOptions.lotPrefix,
    tractPrefix: displayOptions.tractPrefix,
    buildingPrefix: displayOptions.buildingPrefix,
  });

  return (
    <Modal
      visible={true}
      width={isMobileView ? '90%' : '60%'}
      footer={null}
      title={<h3 data-cy="lot-modal-title">{lotTitle}</h3>}
      centered
      onCancel={() => dispatch(setSelectedLotId(''))}
      data-cy="lot-modal"
    >
      {!planElvs || planElvs.length === 0 ? (
        <Empty description="No Plan or Elevations" />
      ) : (
        <Carousel
          responsive={responsive}
          arrows={!isMobileView}
          showDots={planElvs.length === 1 ? false : true}
          draggable={false}
          afterChange={(_, { currentSlide }) => {
            const elevationName = elevations[planElvs[currentSlide].comElevationsSysId].style;
            gaFunction.spViewLotElevation(lotTitle, elevationName, 'false');
          }}
        >
          {planElvs
            .filter((planElv) =>
              filterPlanSysId ? planElv.comPlansSysId === filterPlanSysId : true
            )
            .map((planElv) => (
              <LotInfoCard
                key={planElv.sysId}
                baseUrl={`${BASE_ASSET_URL}/${communityId}`}
                orientation={orientation[lots[selectedLotId].comOrientationSysId]}
                plan={plans[planElv.comPlansSysId]}
                elevation={elevations[planElv.comElevationsSysId]}
                phase={phases[lots[selectedLotId].comPhasesSysId]}
                lot={lots[selectedLotId]}
                lotTitle={lotTitle}
                displayOption={displayOptions}
                isolatePlan={isolatePlan}
                userData={userData}
              />
            ))}
        </Carousel>
      )}
    </Modal>
  );
};

export default LotModal;
